import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Check, ChevronRight, Loader2, Shield, Tag } from "lucide-react";
import { SpurwechselLogo } from "@/components/SpurwechselLogo";
import { toast } from "sonner";

const ALL_FEATURES = [
  "Unbegrenzte Trainings-Sessions",
  "Alle Gesprächsmodi (freundlich bis intensiv)",
  "Prüfungsmodus mit 4 Gutachter-Personas",
  "Aktenanalyse & Dokumenten-Besprechung",
  "Geschichte aufarbeiten (Story-Rekonstruktion)",
  "Vollständige Trainings-Analyse",
  "Automatische Trainingsnotizen",
  "Trainingsfortschritt-Übersicht",
];

const PLANS = [
  { id: "3wochen", name: "3 Wochen", price: "39", days: 21, subtitle: "Für fokussierte Vorbereitung" },
  { id: "6wochen", name: "6 Wochen", price: "59", days: 45, subtitle: "Für gründliche Vorbereitung", highlight: true, badge: "Beliebteste Wahl" },
  { id: "3monate", name: "3 Monate", price: "89", days: 90, subtitle: "Für intensive Vorbereitung" },
];

export default function PricingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentPlan, setCurrentPlan] = useState("free");
  const [loading, setLoading] = useState(null);
  const [promoCode, setPromoCode] = useState("");
  const [promoResult, setPromoResult] = useState(null);
  const [promoChecking, setPromoChecking] = useState(false);

  useEffect(() => {
    if (user) { api.get("/subscription").then(r => setCurrentPlan(r.data.expired ? "free" : r.data.plan)).catch(() => {}); }
  }, [user]);

  const validatePromo = async () => {
    if (!promoCode.trim()) return;
    setPromoChecking(true);
    try {
      const res = await api.post(`/promo/validate?code=${promoCode.trim()}`);
      setPromoResult(res.data);
      toast.success(`${res.data.description} aktiviert!`);
    } catch (err) {
      setPromoResult(null);
      toast.error(err.response?.data?.detail || "Ungültiger Code");
    }
    setPromoChecking(false);
  };

  const getPrice = (originalPrice) => {
    if (promoResult?.discount_percent) {
      const discounted = originalPrice * (1 - promoResult.discount_percent / 100);
      return Math.round(discounted * 100) / 100; // Round to 2 decimal places, matching backend
    }
    return originalPrice;
  };

  const handleCheckout = async (planId) => {
    if (!user) { navigate("/register"); return; }
    setLoading(planId);
    try {
      const payload = { plan: planId, origin_url: window.location.origin };
      if (promoResult?.code) payload.promo_code = promoResult.code;
      const res = await api.post("/payment/checkout", payload);
      window.location.href = res.data.url;
    } catch (err) { toast.error(err.response?.data?.detail || "Checkout fehlgeschlagen"); }
    setLoading(null);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="pricing-page">
      <nav className="glass-nav sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")}>
            <SpurwechselLogo size="small" />
          </div>
          <Button variant="ghost" className="font-body text-sm" onClick={() => navigate(user ? "/dashboard" : "/login")}>
            {user ? "Dashboard" : "Anmelden"}
          </Button>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-3">Einmal zahlen. Kein Abo.</h1>
          <p className="font-body text-base text-muted-foreground max-w-lg mx-auto">
            Wählen Sie Ihren Zeitraum. Alle Features sind in jedem Plan enthalten — keine versteckten Extras.
          </p>
        </div>

        {/* Free */}
        <div className="bg-muted/30 rounded-xl p-4 sm:p-5 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <p className="font-body text-sm font-medium">Kostenloser Einstieg</p>
            <p className="font-body text-xs text-muted-foreground">3 Sessions zum Kennenlernen + alle Wissens-Artikel — ohne Zeitlimit</p>
          </div>
          <Button variant="outline" size="sm" className="font-body" onClick={() => navigate("/register")}>Kostenlos testen</Button>
        </div>

        {/* Promo Code */}
        <div className="max-w-sm mx-auto mb-8">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={promoCode}
                onChange={e => setPromoCode(e.target.value.toUpperCase())}
                onKeyDown={e => { if (e.key === "Enter") validatePromo(); }}
                placeholder="Gutscheincode eingeben"
                className="pl-9 h-10 font-body text-sm"
                data-testid="promo-input"
              />
            </div>
            <Button variant="outline" size="sm" className="font-body h-10 px-4" onClick={validatePromo} disabled={promoChecking || !promoCode.trim()} data-testid="promo-apply">
              {promoChecking ? <Loader2 className="w-4 h-4 animate-spin" /> : "Einlösen"}
            </Button>
          </div>
          {promoResult && (
            <p className="font-body text-xs text-success mt-2 flex items-center gap-1">
              <Check className="w-3 h-3" /> {promoResult.description} — {promoResult.discount_percent}% Rabatt aktiv
            </p>
          )}
        </div>

        {/* Plans */}
        <div className="grid md:grid-cols-3 gap-5 mb-10">
          {PLANS.map((plan) => {
            const discountedPrice = getPrice(parseInt(plan.price));
            const hasDiscount = discountedPrice < parseInt(plan.price);
            return (
            <Card key={plan.id} className={`border-border/50 relative ${plan.highlight ? "border-primary shadow-lg ring-1 ring-primary/20" : ""}`} data-testid={`plan-card-${plan.id}`}>
              {plan.badge && <div className="absolute -top-3 left-1/2 -translate-x-1/2"><Badge className="bg-primary text-primary-foreground font-body text-xs px-3 py-1">{plan.badge}</Badge></div>}
              <CardHeader className="text-center pb-2 pt-6">
                <CardTitle className="font-heading text-xl">{plan.name}</CardTitle>
                <p className="font-body text-xs text-muted-foreground">{plan.subtitle}</p>
                <div className="mt-3">
                  {hasDiscount && <span className="font-body text-lg text-muted-foreground line-through mr-2">€{plan.price}</span>}
                  <span className="font-heading text-4xl font-bold">{hasDiscount ? `€${Number.isInteger(discountedPrice) ? discountedPrice : discountedPrice.toFixed(2).replace('.', ',')}` : `€${plan.price}`}</span>
                  <span className="font-body text-sm text-muted-foreground ml-1">einmalig</span>
                </div>
                <p className="font-body text-xs text-primary font-medium mt-1">{plan.days} Tage Vollzugang</p>
              </CardHeader>
              <CardContent>
                {currentPlan === plan.id ? (
                  <Button disabled className="w-full font-body" variant="secondary">Aktueller Zugang</Button>
                ) : (
                  <Button className={`w-full font-body gap-1 ${plan.highlight ? "bg-primary text-primary-foreground hover:bg-primary/90" : ""}`}
                    variant={plan.highlight ? "default" : "outline"} onClick={() => handleCheckout(plan.id)} disabled={loading === plan.id} data-testid={`plan-btn-${plan.id}`}>
                    {loading === plan.id && <Loader2 className="w-4 h-4 animate-spin" />}
                    Zugang kaufen <ChevronRight className="w-4 h-4" />
                  </Button>
                )}
              </CardContent>
            </Card>
          );
          })}
        </div>

        {/* All Features */}
        <div className="max-w-2xl mx-auto">
          <h2 className="font-heading text-xl font-bold text-center mb-6">In jedem Plan enthalten</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {ALL_FEATURES.map((f, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <Check className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                <span className="font-body text-sm">{f}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Comparison */}
        <div className="max-w-2xl mx-auto mt-12 mb-10">
          <h2 className="font-heading text-xl font-bold text-center mb-6">Wie sich Spurwechsel vergleicht</h2>
          <div className="overflow-x-auto">
            <table className="w-full font-body text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 pr-4 font-medium text-muted-foreground"></th>
                  <th className="text-center py-3 px-3 font-medium text-muted-foreground">Einzelberatung</th>
                  <th className="text-center py-3 px-3 font-medium text-muted-foreground">Online-Kurse</th>
                  <th className="text-center py-3 px-3 font-semibold text-primary">Spurwechsel</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Preis", "€800–2.200", "€200–300", "€39–89"],
                  ["Aktives Gesprächstraining", "Begrenzt", "Nein", "Unbegrenzt"],
                  ["Verfügbarkeit", "Termingebunden", "Flexibel", "24/7"],
                  ["Gutachter-Simulation", "1x inklusive", "Nein", "4 Persönlichkeiten"],
                  ["Aktenanalyse", "Ab €1.400", "Nein", "Inklusive"],
                  ["Automatisches Feedback", "Nein", "Nein", "Nach jeder Session"],
                ].map(([label, col1, col2, col3], i) => (
                  <tr key={i} className="border-b border-border/50">
                    <td className="py-2.5 pr-4 font-medium">{label}</td>
                    <td className="py-2.5 px-3 text-center text-muted-foreground">{col1}</td>
                    <td className="py-2.5 px-3 text-center text-muted-foreground">{col2}</td>
                    <td className="py-2.5 px-3 text-center font-medium text-primary">{col3}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="font-body text-xs text-muted-foreground text-center mt-4">Spurwechsel ergänzt professionelle Beratung — es ersetzt sie nicht.</p>
        </div>

        {/* Trust */}
        <div className="mt-10 text-center space-y-4">
          <div className="flex flex-wrap justify-center gap-4 sm:gap-8">
            {["Kein Abo", "Keine Kündigungsfrist", "Sichere Zahlung", "DSGVO-konform"].map((t, i) => (
              <div key={i} className="flex items-center gap-1.5"><Shield className="w-3.5 h-3.5 text-primary" /><span className="font-body text-xs text-muted-foreground">{t}</span></div>
            ))}
          </div>
          <p className="font-body text-xs text-muted-foreground">Übungswerkzeug — ersetzt keine professionelle MPU-Beratung oder Therapie.</p>
        </div>
      </div>
    </div>
  );
}
